// src/firebase.js
// Inicialización Firebase (modular) — persistence + authReady export + diagnostics helper.
//
// Ajuste clave (iOS/Capacitor):
// - NO "skip" persistence siempre.
// - Intentar indexedDBLocalPersistence con timeout corto.
// - Fallback a inMemoryPersistence si WKWebView bloquea IndexedDB/localStorage.

import { initializeApp, getApps } from "firebase/app";
import { initializeLoopAppCheck } from "./appCheckBootstrap";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  onAuthStateChanged,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getMessaging, isSupported } from "firebase/messaging";

import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";

/* env / config */
const env = typeof process !== "undefined" ? process.env : {};

const firebaseConfig = {
  apiKey: env.REACT_APP_FIREBASE_API_KEY || "AIzaSyAX2UN-Iqn2LIk2nftv8evqCibnsZfH8qc",
  authDomain: env.REACT_APP_FIREBASE_AUTH_DOMAIN || "auth.loopreservas.com",
  projectId: env.REACT_APP_FIREBASE_PROJECT_ID || "web-app-d9799",
  storageBucket: env.REACT_APP_FIREBASE_STORAGE_BUCKET || "web-app-d9799.firebasestorage.app",
  messagingSenderId: env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "357961918175",
  appId: env.REACT_APP_FIREBASE_APP_ID || "1:357961918175:web:59bcac223ef4de6b18ab6a",
  measurementId: env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-RJ70PF8YJ7",
};

/* init app (idempotent) */
let app;
if (!getApps() || getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Register native App Check with both the native layer and
// the Firebase JavaScript SDK before creating Auth, Firestore and Storage.
export const appCheckReady = initializeLoopAppCheck(app);

/* runtime heuristics */
const isRunningInCapacitorWebView = (() => {
  try {
    if (typeof window === "undefined") return false;
    if (!window.Capacitor) return false;
    const platform =
      typeof window.Capacitor.getPlatform === "function"
        ? window.Capacitor.getPlatform()
        : window.Capacitor.platform || null;
    return !!(platform && platform !== "web");
  } catch (e) {
    return false;
  }
})();

const isIosWebView = (() => {
  try {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isIOS = /iP(hone|od|ad)/i.test(ua);
    const isSafari = /Version\/\d+.*Safari|Safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    return isIOS && !isSafari;
  } catch (e) {
    return false;
  }
})();

const isAndroidCapacitorWebView = (() => {
  try {
    if (typeof window === "undefined" || !window.Capacitor) return false;
    const platform =
      typeof window.Capacitor.getPlatform === "function"
        ? window.Capacitor.getPlatform()
        : window.Capacitor.platform || null;
    return platform === "android";
  } catch (e) {
    return false;
  }
})();


/* better error formatter */
function fmtErr(err) {
  if (!err) return String(err);
  try {
    if (err && (err.code || err.message)) return `${err.code || ""} ${err.message || ""}`.trim();
    if (typeof err === "string") return err;
    const seen = new WeakSet();
    return JSON.stringify(err, function (k, v) {
      if (v && typeof v === "object") {
        if (seen.has(v)) return "[Circular]";
        seen.add(v);
      }
      return v;
    });
  } catch (_) {
    try {
      return String(err);
    } catch (_) {
      return "unknown error";
    }
  }
}

/* authReady promise */
let _authReadyResolve, _authReadyReject;
export const authReady = new Promise((resolve, reject) => {
  _authReadyResolve = resolve;
  _authReadyReject = reject;
});

/* helper: setPersistence with timeout and readable errors */
async function _trySetPersistence(authInstance, persistence, timeoutMs = 4000, strategyName = "unknown") {
  if (!authInstance || !persistence) return { ok: false, err: "no-auth-or-persistence", strategy: strategyName };
  let timer;
  try {
    const p = setPersistence(authInstance, persistence);
    await Promise.race([
      p,
      new Promise((_, rej) => {
        timer = setTimeout(() => rej(new Error("persistence-timeout")), timeoutMs);
      }),
    ]);
    return { ok: true, err: null, strategy: strategyName };
  } catch (err) {
    return { ok: false, err: fmtErr(err), strategy: strategyName };
  } finally {
    try {
      if (timer) clearTimeout(timer);
    } catch (_) {}
  }
}

/* create auth and try persistence options */
function createAuthInstance() {
  const authInstance = getAuth(app);

  (async () => {
    try {
      console.log("[firebase] starting persistence setup", { isRunningInCapacitorWebView, isIosWebView, isAndroidCapacitorWebView });
      const persistenceDetail = { applied: false, strategy: null, attempts: [] };

      // Android Capacitor: skip the custom startup persistence sequence.
      if (isAndroidCapacitorWebView) {
        persistenceDetail.applied = true;
        persistenceDetail.strategy = "android-native-skip-custom-persistence";
        console.log("[firebase] Android Capacitor detected -> skipping custom setPersistence at startup");
      } else if (isIosWebView || isRunningInCapacitorWebView) {
        const tryOrder = [
          { persistence: indexedDBLocalPersistence, name: "indexedDBLocalPersistence", timeout: 2500 },
          { persistence: browserLocalPersistence, name: "browserLocalPersistence", timeout: 2500 },
          { persistence: inMemoryPersistence, name: "inMemoryPersistence", timeout: 1500 },
        ];

        for (const it of tryOrder) {
          const res = await _trySetPersistence(authInstance, it.persistence, it.timeout, it.name);
          persistenceDetail.attempts.push(res);
          if (res.ok) {
            persistenceDetail.applied = true;
            persistenceDetail.strategy = res.strategy;
            console.log(`[firebase] applied ${res.strategy}`);
            break;
          } else {
            console.warn(`[firebase] ${res.strategy} failed: ${res.err}`);
          }
        }
      } else {
        // Desktop web: FORCE local persistence first (avoid logout on refresh)
        const tryOrder = [
          { persistence: browserLocalPersistence, name: "browserLocalPersistence", timeout: 5000 },
          { persistence: indexedDBLocalPersistence, name: "indexedDBLocalPersistence", timeout: 3500 },
        ];
        for (const it of tryOrder) {
          const res = await _trySetPersistence(authInstance, it.persistence, it.timeout, it.name);
          persistenceDetail.attempts.push(res);
          if (res.ok) {
            persistenceDetail.applied = true;
            persistenceDetail.strategy = res.strategy;
            console.log(`[firebase] applied ${res.strategy}`);
            break;
          } else {
            console.warn(`[firebase] ${res.strategy} failed: ${res.err}`);
          }
        }
      }

      // Expose for UI diagnostics
      try {
        if (typeof window !== "undefined") {
          window.__FIREBASE_PERSISTENCE__ = persistenceDetail;
        }
      } catch (e) {}

      console.log("[firebase] persistence setup finished", persistenceDetail);
      try {
        _authReadyResolve(true);
      } catch (_) {}
    } catch (err) {
      console.warn("[firebase] persistence setup error:", fmtErr(err));
      try {
        _authReadyReject(err);
      } catch (_) {}
      try {
        _authReadyResolve(false);
      } catch (_) {}
    }
  })();

  // central onAuthStateChanged trace for diagnostics
  try {
    if (typeof window !== "undefined") {
      onAuthStateChanged(authInstance, (u) => {
        try {
          window.__AUTH_STATE_LOG__ = window.__AUTH_STATE_LOG__ || [];
          window.__AUTH_STATE_LOG__.push({ time: new Date().toISOString(), uid: u ? u.uid : null });
        } catch (e) {}
        console.debug("[firebase] onAuthStateChanged ->", u ? u.uid : null);
      });
    }
  } catch (e) {
    console.warn("[firebase] onAuthStateChanged attach failed:", fmtErr(e));
  }

  return authInstance;
}

/* exports */
export const auth = createAuthInstance();
export const firebaseAuth = auth;
export const db = getFirestore(app);
export const storage = getStorage(app);

let messaging = null;
export const messagingReady = (async () => {
  if (typeof window === "undefined" || isRunningInCapacitorWebView) return null;
  try {
    if (await isSupported()) messaging = getMessaging(app);
  } catch (err) {
    console.warn("[firebase] getMessaging not available:", fmtErr(err));
    messaging = null;
  }
  return messaging;
})();
export { messaging };

export const firebaseConfigRuntime = firebaseConfig;
export const VAPID_KEY = env.REACT_APP_FIREBASE_VAPID_KEY || "";

export function getFirebaseInfo() {
  return {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    apiKeySet: Boolean(firebaseConfig.apiKey && !String(firebaseConfig.apiKey).includes("<REPLACE")),
    appIdSet: Boolean(firebaseConfig.appId && !String(firebaseConfig.appId).includes("<REPLACE")),
    fullConfig: firebaseConfig,
  };
}

/* re-export some firestore helpers */
export { collection, collectionGroup, query, where, orderBy, getDocs, onSnapshot, doc, updateDoc };

/* expose debug vars on window for remote inspection / UI diagnostics */
console.log("DEBUG firebase initialized:", {
  projectId: firebaseConfig.projectId,
  appId: firebaseConfig.appId,
  isRunningInCapacitorWebView,
  isIosWebView,
  isAndroidCapacitorWebView,
});
if (typeof window !== "undefined") {
  try {
    window.__APP_AUTH__ = auth;
    window.__APP_DB__ = db;
    window.__FIREBASE_CONFIG__ = firebaseConfig;
  } catch (e) {
    console.warn("Couldn't expose debug vars on window:", e);
  }

  // BUILD STAMP: confirms the JS bundle is the one you expect on TestFlight.
  try {
    window.__BUILD_STAMP__ = {
      time: new Date().toISOString(),
      projectId: firebaseConfig.projectId,
    };
  } catch (_) {}
}

/**
 * ensureInMemoryPersistenceNow(timeoutMs)
 */
export async function ensureInMemoryPersistenceNow(timeoutMs = 1500) {
  try {
    const authInstance = firebaseAuth;
    if (!authInstance) {
      return { ok: false, err: "no-auth-instance" };
    }
    const res = await Promise.race([
      setPersistence(authInstance, inMemoryPersistence).then(() => ({ ok: true, strategy: "inMemoryPersistence" })),
      new Promise((_, rej) => setTimeout(() => rej(new Error("persistence-timeout")), timeoutMs)),
    ]);
    try {
      if (typeof window !== "undefined") {
        window.__INMEM_TRY__ = { result: res, time: new Date().toISOString() };
      }
    } catch (_) {}
    return res;
  } catch (err) {
    const e = fmtErr(err);
    try {
      if (typeof window !== "undefined") {
        window.__INMEM_TRY__ = { result: { ok: false, err: e }, time: new Date().toISOString() };
      }
    } catch (_) {}
    return { ok: false, err: e };
  }
}

export default app;
